import React, { useContext, useMemo, useRef } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { IntlContext, AuthContext } from "context";
import { Calendar, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { PhoneField, RadixSelect, TextField, Textarea } from "components/Form/Field";
import Button from "ui/Button";
import Address from "components/Form/Address";
import { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import type { RegisterDraft } from "../types";
import { validateRegisterProviderInformationsProfile } from "hooks/register";
import { useReferences } from "hooks/reference";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";
import { isValidPhone } from "helpers/phone/phoneNumber";
import { fieldLabelWithTooltip } from "../common/FieldInfoTooltip";
import {
    DateInputWithPicker,
    FRANCE_BIRTH_COUNTRY_CODE_ISO,
    ProviderBirthDepartmentFromNir,
    ProviderDefaultBirthCountry,
    fieldInputClassName,
    fieldLabelClassName,
    isNonEmptyString,
    isNirConsistentWithBirthData,
    isPlausibleFrenchNir,
    getNirBirthConsistencyErrors,
    sectionIndexClassName,
    sectionTitleClassName,
    textareaClassName,
} from "./infosShared";

interface Props {
    provider: RegisterDraft | undefined;
    setProvider: (provider: RegisterDraft | undefined) => void;
}

const RegisterProviderInfosProfile: React.FC<Props> = ({ provider, setProvider }) => {
    const intl = useContext(IntlContext);
    const auth = useContext(AuthContext);
    const stepper = useRegisterStepper();
    const reference = useReferences();
    const countries = reference.all("countries");
    const birthdayInputRef = useRef<DateInputWithPicker>(null);

    const openBirthdayPicker = () => {
        const el = birthdayInputRef.current;
        if (!el) return;
        el.focus();
        el.showPicker?.();
    };

    const countryBirthOptions = useMemo(() => {
        const rows = countries
            .filter((c) => typeof c.code_iso === "string" && String(c.code_iso).trim().length > 0)
            .map((c) => ({
                label: `${c.value.charAt(0).toUpperCase()}${c.value.slice(1).toLowerCase()}`,
                value: String(c.code_iso).trim(),
                deleted: Boolean(c.deleted_at),
            }));

        rows.sort((a, b) => {
            if (a.deleted !== b.deleted) {
                return Number(a.deleted) - Number(b.deleted);
            }
            return a.label.localeCompare(b.label, "fr");
        });

        const byIso = new Map<string, { label: string; value: string }>();
        for (const row of rows) {
            if (!byIso.has(row.value)) {
                byIso.set(row.value, { label: row.label, value: row.value });
            }
        }

        return Array.from(byIso.values()).sort((a, b) => a.label.localeCompare(b.label, "fr"));
    }, [countries]);

    useRegisterDraftGuard(provider, "prestations");

    const onSubmit = async (values: RegisterDraft) => {
        try {
            const data = await validateRegisterProviderInformationsProfile(values);
            setProvider(Object.assign({}, provider, data));
            trackRegisterStepComplete({
                registerType: "provider",
                stepKey: "informations_profile",
                stepIndex: 4,
                stepLabel: intl.formatMessage("authentication.register.provider_flow.infos_profile.label"),
            });
            stepper.goTo("photo");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    ...(provider ?? {}),
                    country_birth_code: provider?.country_birth_code ?? FRANCE_BIRTH_COUNTRY_CODE_ISO,
                }}
                mutators={{ ...arrayMutators }}
                validate={(values: RegisterDraft) => {
                    const errors: Record<string, string> = {};

                    if (!auth.account?.user.roles) {
                        const nir = String(values?.nir ?? "");

                        if (nir.trim() && !isPlausibleFrenchNir(nir)) {
                            errors.nir = intl.formatMessage("register.input.nir.error");
                        }

                        Object.assign(
                            errors,
                            getNirBirthConsistencyErrors(values, {
                                birthdayMismatch: intl.formatMessage("register.input.nir.birthday_mismatch"),
                                departmentMismatch: intl.formatMessage("register.input.nir.department_mismatch"),
                            }),
                        );
                    }

                    return errors;
                }}
                render={({ handleSubmit, submitting, values, hasValidationErrors }) => {
                    const descriptionLength = String(values?.description ?? "").length;
                    const descriptionMinLength = 50;
                    const descriptionMaxLength = 2000;
                    const hasMinDescriptionLength = descriptionLength >= descriptionMinLength;

                    const hasExistingRoles = Boolean(auth.account?.user.roles);
                    const isFranceBirth =
                        String(values?.country_birth_code ?? "").trim() === FRANCE_BIRTH_COUNTRY_CODE_ISO;

                    const canGoNext =
                        hasExistingRoles ||
                        (isValidPhone(values?.phone) &&
                            isNonEmptyString(values?.nir) &&
                            isPlausibleFrenchNir(String(values?.nir ?? "")) &&
                            isNirConsistentWithBirthData(values) &&
                            isNonEmptyString(values?.birthday) &&
                            isNonEmptyString(values?.country_birth_code) &&
                            isNonEmptyString(values?.city_birth) &&
                            (!isFranceBirth || isNonEmptyString(values?.department_birth_code)) &&
                            isNonEmptyString(values?.address) &&
                            isNonEmptyString(values?.coordinates_lat) &&
                            isNonEmptyString(values?.coordinates_lon) &&
                            isNonEmptyString(values?.house_number) &&
                            isNonEmptyString(values?.street) &&
                            isNonEmptyString(values?.postcode) &&
                            isNonEmptyString(values?.city) &&
                            isNonEmptyString(values?.commune_code) &&
                            hasMinDescriptionLength);

                    const todayIso = new Date().toISOString().slice(0, 10);
                    const birthPlaceHelp = intl.formatMessage("register.label.birth.place.help");

                    return (
                        <form onSubmit={handleSubmit} className="register__form">
                            <div className="flex flex--direction-column gap-10">
                                <p className="h1 font-black">
                                    Complétez <span className="block text-primary">votre profil</span>
                                </p>
                                <p className="h4 margin-bottom--2 margin-top--1">
                                    Ces informations vous permettent d'avoir 2x plus de visibilité auprès de vos voisins.
                                </p>

                                {!auth.account?.user.roles && (
                                    <div className="register__group-fields">
                                        <div className="space-y-4">
                                            <div className={sectionTitleClassName}>
                                                <span className={sectionIndexClassName}>1</span>
                                                <span className="font-black">Mes informations</span>
                                            </div>

                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <PhoneField
                                                    name="phone"
                                                    required
                                                    placeholder="6 12 34 56 78"
                                                    label={fieldLabelWithTooltip(
                                                        intl.formatMessage("authentication.register.label.phone"),
                                                        intl.formatMessage("register.label.phone.tooltip"),
                                                    )}
                                                    fieldLabelClassName={fieldLabelClassName}
                                                    fieldInputClassName={fieldInputClassName}
                                                />
                                                <TextField
                                                    inputProps={{
                                                        name: "nir",
                                                        required: true,
                                                        placeholder: "XXXXXXXXX",
                                                        maxLength: 255,
                                                        className: fieldInputClassName,
                                                    }}
                                                    labelProps={{
                                                        label: fieldLabelWithTooltip(
                                                            intl.formatMessage("profile.label.nir"),
                                                            intl.formatMessage("register.label.nir.tooltip"),
                                                        ),
                                                        className: fieldLabelClassName,
                                                    }}
                                                    infoMessage={intl.formatMessage("register.label.nir.help")}
                                                />
                                                <TextField
                                                    inputProps={{
                                                        ref: birthdayInputRef,
                                                        name: "birthday",
                                                        required: true,
                                                        type: "date",
                                                        max: todayIso,
                                                        className: `${fieldInputClassName} date-input--placeholder pr-12`,
                                                    }}
                                                    labelProps={{
                                                        label: intl.formatMessage(
                                                            "authentication.register.label.birthday"
                                                        ),
                                                        className: fieldLabelClassName,
                                                    }}
                                                    adornmentEnd={
                                                        <button
                                                            type="button"
                                                            className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-foreground"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={openBirthdayPicker}
                                                            aria-label="Open calendar"
                                                        >
                                                            <Calendar className="h-4 w-4" />
                                                        </button>
                                                    }
                                                />
                                                <RadixSelect
                                                    inputProps={{
                                                        name: "country_birth_code",
                                                        placeholder: intl.formatMessage("register.label.country.birth"),
                                                        required: true,
                                                        className: fieldInputClassName,
                                                        options: countryBirthOptions,
                                                    }}
                                                    labelProps={{
                                                        label: intl.formatMessage("register.label.country.birth"),
                                                        className: fieldLabelClassName,
                                                    }}
                                                />
                                                {isFranceBirth && (
                                                    <TextField
                                                        inputProps={{
                                                            name: "department_birth_code",
                                                            required: isFranceBirth,
                                                            maxLength: 3,
                                                            placeholder: "ex. 75, 2A, 2B",
                                                            className: fieldInputClassName,
                                                        }}
                                                        labelProps={{
                                                            label: intl.formatMessage("register.label.department.birth"),
                                                            className: fieldLabelClassName,
                                                        }}
                                                    />
                                                )}
                                                <TextField
                                                    inputProps={{
                                                        name: "city_birth",
                                                        required: true,
                                                        maxLength: 30,
                                                        placeholder: "ex: Nantes",
                                                        className: fieldInputClassName,
                                                    }}
                                                    labelProps={{
                                                        label: intl.formatMessage("register.label.city.birth"),
                                                        className: fieldLabelClassName,
                                                    }}
                                                />
                                                <p className="sm:col-span-2 field__feedback leading-relaxed">{birthPlaceHelp}</p>
                                            </div>
                                            <ProviderDefaultBirthCountry />
                                            <ProviderBirthDepartmentFromNir />
                                        </div>
                                    </div>
                                )}

                                {!auth.account?.user.roles && (
                                    <div className="register__group-fields">
                                        <div className="space-y-4">
                                            <div className={sectionTitleClassName}>
                                                <span className={sectionIndexClassName}>2</span>
                                                <span className="font-black">Confirmer ma localisation</span>
                                            </div>
                                            <div className="grid--12 register__grid grid--has-gutter-3x">
                                                <div className="grid__col--12 grid__col--small-12">
                                                    <Address
                                                        inputProps={{
                                                            name: "address",
                                                            latitudeName: "coordinates_lat",
                                                            longitudeName: "coordinates_lon",
                                                            houseNumber: "house_number",
                                                            street: "street",
                                                            postcode: "postcode",
                                                            city: "city",
                                                            commune_code: "commune_code",
                                                            required: true,
                                                        }}
                                                        labelProps={{
                                                            label: intl.formatMessage(
                                                                "authentication.register.label.address"
                                                            ),
                                                            className: fieldLabelClassName,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {!auth.account?.user.roles && (
                                    <div className="register__group-fields">
                                        <div className="space-y-4">
                                            <div className={sectionTitleClassName}>
                                                <span className={sectionIndexClassName}>3</span>
                                                <span className="font-black">Ma présentation</span>
                                            </div>

                                            <Textarea
                                                inputProps={{
                                                    name: "description",
                                                    required: true,
                                                    minLength: descriptionMinLength,
                                                    maxLength: descriptionMaxLength,
                                                    placeholder:
                                                        "Présentez-vous à vos voisins : votre expérience, vos motivations, ce que vous aimez dans les services que vous proposez...",
                                                    className: textareaClassName,
                                                }}
                                                labelProps={{
                                                    label: "Description détaillée de votre profil",
                                                    className: fieldLabelClassName,
                                                }}
                                            />

                                            <div className="flex items-center justify-between gap-4 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                                <div className="flex items-center gap-2">
                                                    <Sparkles className="h-3 w-3" /> Inspirez confiance !
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div>
                                                        <span className="text-primary">{descriptionLength}</span>/
                                                        {descriptionMaxLength} caractères
                                                    </div>
                                                    <div>
                                                        Min.{" "}
                                                        <span
                                                            className={
                                                                hasMinDescriptionLength
                                                                    ? "text-emerald-600"
                                                                    : "text-red-600"
                                                            }
                                                        >
                                                            {descriptionMinLength}
                                                        </span>{" "}
                                                        caractères
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center justify-between text-center margin-top--10">
                                    <Button
                                        className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                        onClick={() => {
                                            setProvider(Object.assign({}, provider, values));
                                            stepper.goTo("informations");
                                        }}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                        {intl.formatMessage("generic.step.previous")}
                                    </Button>
                                    <Button
                                        className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full font-black px-10 py-4"
                                        type="submit"
                                        disabled={submitting || Boolean(hasValidationErrors) || !canGoNext}
                                        data-analytics-id="register-provider-informations-profile-next"
                                    >
                                        {intl.formatMessage("generic.step.next")}
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </form>
                    );
                }}
            />
        </React.Fragment>
    );
};

export default RegisterProviderInfosProfile;
